import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Can } from '../components/Can';
import {
  Brain, Scale, Users, TrendingUp, TrendingDown, Minus, Play, Loader2,
  Search, Database, Activity, CheckCircle, AlertTriangle, DollarSign, Clock, Gavel,
} from 'lucide-react';

// The brain's firm pipeline, in order. Plumbing nodes (tool calls, message cleanup) map to
// stage "other" and are shown as a quiet "gathering data" counter rather than as agent cards.
const STAGES = [
  { key: 'analyst', label: 'Analysts', icon: Search, hint: 'Market · Social · News · Fundamentals' },
  { key: 'research_debate', label: 'Research Debate', icon: Users, hint: 'Bull vs Bear → Research Manager' },
  { key: 'trader', label: 'Trader', icon: Activity, hint: 'Turns the thesis into an action' },
  { key: 'risk_debate', label: 'Risk Debate', icon: AlertTriangle, hint: 'Aggressive · Conservative · Neutral' },
  { key: 'decision', label: 'Portfolio Manager', icon: Gavel, hint: 'Final firm rating' },
];

function headers() {
  return { Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' };
}

// Map any firm's rating vocabulary to a bull/bear/neutral tone for coloring.
function tone(action) {
  const a = String(action || '').toUpperCase();
  if (/(BUY|OVERWEIGHT|BULL)/.test(a) && !/UNDER/.test(a)) return 'bull';
  if (/(AVOID|SELL|UNDERWEIGHT|BEAR)/.test(a)) return 'bear';
  return 'neutral';
}
const toneClasses = {
  bull: 'text-green-400 border-green-500/40 bg-green-500/10',
  bear: 'text-red-400 border-red-500/40 bg-red-500/10',
  neutral: 'text-yellow-400 border-yellow-500/40 bg-yellow-500/10',
};
function ToneIcon({ action, className }) {
  const t = tone(action);
  if (t === 'bull') return <TrendingUp className={className} />;
  if (t === 'bear') return <TrendingDown className={className} />;
  return <Minus className={className} />;
}

function VerdictCard({ title, icon: Icon, action, sub, detail, loading, highlight }) {
  const t = tone(action);
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-2 ${highlight
      ? 'border-purple-500/50 bg-purple-500/10 ring-1 ring-purple-500/30'
      : 'border-gray-700 bg-gray-800/40'}`}>
      <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider font-bold">
        <Icon className="w-4 h-4" /> {title}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-2"><Loader2 className="w-4 h-4 animate-spin" /> deliberating…</div>
      ) : action ? (
        <>
          <div className={`inline-flex items-center gap-2 self-start px-3 py-1 rounded-lg border font-bold ${toneClasses[t]}`}>
            <ToneIcon action={action} className="w-4 h-4" /> {action}
          </div>
          {sub && <div className="text-sm text-gray-300">{sub}</div>}
          {detail && <div className="text-xs text-gray-500 leading-relaxed">{detail}</div>}
        </>
      ) : (
        <div className="text-gray-600 text-sm py-2">—</div>
      )}
    </div>
  );
}

function AgentStep({ node, content }) {
  return (
    <div className="rounded-lg border border-gray-700/60 bg-gray-900/40 p-3">
      <div className="text-xs font-bold text-blue-300 mb-1">{node}</div>
      <div className="text-xs text-gray-400 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
        {content}
      </div>
    </div>
  );
}

export default function WarRoom() {
  const { user } = useAuth();
  const [ticker, setTicker] = useState('');
  const [running, setRunning] = useState(false);
  const [activeTicker, setActiveTicker] = useState(null);
  const [steps, setSteps] = useState([]);          // brain agent_step events (non-plumbing)
  const [plumbing, setPlumbing] = useState(0);     // count of tool/cleanup events
  const [paeru, setPaeru] = useState(null);
  const [brain, setBrain] = useState(null);
  const [arbiter, setArbiter] = useState(null);
  const [finalAction, setFinalAction] = useState(null);
  const [error, setError] = useState(null);
  const [wsReady, setWsReady] = useState(false);
  const [decisions, setDecisions] = useState([]);
  const wsRef = useRef(null);
  const feedRef = useRef(null);
  const runStartRef = useRef(0);

  // Populate the verdict cards from a stored ledger record (used for the poll-fallback and
  // for clicking a past decision).
  const applyRecord = useCallback((rec) => {
    setActiveTicker(rec.ticker);
    setPaeru(rec.paeru || null);
    setBrain(rec.brain || null);
    setArbiter(rec.arbiter || null);
    setFinalAction((rec.arbiter && rec.arbiter.decision) || rec.final_action || null);
    setSteps((rec.brain && rec.brain.reports) ? rec.brain.reports : []);
    setRunning(false);
  }, []);

  const openDecision = useCallback(async (id) => {
    try {
      const r = await fetch(`/api/warroom/decisions/${id}`, { headers: headers() });
      if (r.ok) applyRecord(await r.json());
    } catch (_) {}
  }, [applyRecord]);

  const loadDecisions = useCallback(async () => {
    try {
      const r = await fetch('/api/warroom/decisions?limit=25', { headers: headers() });
      if (r.ok) setDecisions(await r.json());
    } catch (_) {}
  }, []);

  const handleEvent = useCallback((ev) => {
    switch (ev.type) {
      case 'orchestrator':
        if (ev.phase === 'done') {
          setRunning(false);
          if (ev.final_action) setFinalAction(ev.final_action);
          loadDecisions();
        }
        break;
      case 'agent_step':
        if (ev.stage === 'other') setPlumbing((p) => p + 1);
        else setSteps((s) => [...s, ev]);
        break;
      case 'paeru_verdict': setPaeru(ev.verdict); break;
      case 'done': setBrain(ev); break;                 // brain firm's final event
      case 'arbiter_verdict': setArbiter(ev.verdict); setFinalAction(ev.verdict.decision); break;
      case 'paeru_error': case 'brain_error': case 'arbiter_error': case 'fatal':
        setError(`${ev.type}: ${ev.message}`); break;
      default: break;
    }
  }, [loadDecisions]);

  // Persistent dashboard WebSocket; authenticates then listens for relayed war-room events.
  useEffect(() => {
    if (!user?.id) return undefined;
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}/ws`);
    wsRef.current = ws;
    ws.onopen = () => { ws.send(JSON.stringify({ type: 'auth', userId: user.id })); setWsReady(true); };
    ws.onclose = () => setWsReady(false);
    ws.onmessage = (msg) => {
      let data; try { data = JSON.parse(msg.data); } catch { return; }
      if (data.type === 'warroom') handleEvent(data.event);
    };
    return () => { try { ws.close(); } catch (_) {} };
  }, [user?.id, handleEvent]);

  useEffect(() => { loadDecisions(); }, [loadDecisions]);
  useEffect(() => { if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight; }, [steps]);

  // Fallback: while a run is in flight, poll the ledger. The backend writes the record the
  // instant it finishes — so even if the live stream drops, the result resolves within ~15s
  // instead of spinning forever.
  useEffect(() => {
    if (!running) return undefined;
    const iv = setInterval(async () => {
      try {
        const r = await fetch('/api/warroom/decisions?limit=8', { headers: headers() });
        if (!r.ok) return;
        const rows = await r.json();
        setDecisions(rows);
        const done = rows.find((d) => d.ticker === activeTicker && new Date(d.ts).getTime() >= runStartRef.current - 2000);
        if (done) applyRecord(done);
      } catch (_) {}
    }, 15000);
    return () => clearInterval(iv);
  }, [running, activeTicker, applyRecord]);

  const analyze = async () => {
    const t = ticker.toUpperCase().trim();
    if (!t || running) return;
    runStartRef.current = Date.now();
    setRunning(true); setActiveTicker(t); setError(null);
    setSteps([]); setPlumbing(0); setPaeru(null); setBrain(null); setArbiter(null); setFinalAction(null);
    try {
      const r = await fetch('/api/warroom/analyze', { method: 'POST', headers: headers(), body: JSON.stringify({ ticker: t }) });
      if (!r.ok) { setError('Failed to start analysis'); setRunning(false); }
    } catch (e) { setError(String(e.message || e)); setRunning(false); }
  };

  const stepsByStage = (key) => steps.filter((s) => s.stage === key);

  return (
    <div className="p-6 max-w-7xl mx-auto text-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="w-6 h-6 text-purple-400" /> War Room
          </h1>
          <p className="text-sm text-gray-500">Two independent firms deliberate; a blind Arbiter decides. Every run is recorded for learning.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${wsReady ? 'bg-green-400' : 'bg-gray-600'}`} title={wsReady ? 'live' : 'disconnected'} />
          <input
            value={ticker}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && analyze()}
            placeholder="Ticker (e.g. AMD)"
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-40 focus:outline-none focus:border-purple-500"
          />
          <Can perm="action.warroom.analyze" fallback={
            <span className="text-xs text-gray-500 px-2" title="Your role can't run AI war-room analysis (costs money)">View only</span>
          }>
            <button
              onClick={analyze}
              disabled={running || !ticker.trim()}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 px-4 py-2 rounded-lg text-sm font-bold">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {running ? 'Deliberating…' : 'Analyze'}
            </button>
          </Can>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-2 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Verdicts */}
      {activeTicker && (
        <div className="mb-6">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Verdicts — {activeTicker}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <VerdictCard
              title="Paeru V33" icon={Users}
              action={paeru?.action}
              sub={paeru ? `${paeru.confidence}% confidence` : null}
              detail={paeru?.reasoning}
              loading={running && !paeru} />
            <VerdictCard
              title="TradingAgents Brain" icon={Brain}
              action={brain?.decision}
              sub={brain ? `$${brain.cost_usd}${brain.elapsed_sec ? ` · ${brain.elapsed_sec}s` : ''}` : null}
              loading={running && !brain} />
            <VerdictCard
              title="Arbiter — Final" icon={Scale} highlight
              action={arbiter?.decision || finalAction}
              sub={arbiter ? `conviction ${arbiter.conviction} · leaned ${arbiter.leaned_toward}` : null}
              detail={arbiter?.rationale}
              loading={running && !arbiter} />
          </div>
        </div>
      )}

      {/* Live deliberation feed */}
      {activeTicker && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-500 uppercase tracking-wider">Live Deliberation</div>
            {plumbing > 0 && (
              <div className="text-xs text-gray-600 flex items-center gap-1">
                <Database className="w-3 h-3" /> {plumbing} data-gathering steps
              </div>
            )}
          </div>
          <div ref={feedRef} className="grid grid-cols-1 lg:grid-cols-5 gap-3 overflow-x-auto">
            {STAGES.map((stage) => {
              const items = stepsByStage(stage.key);
              const Icon = stage.icon;
              return (
                <div key={stage.key} className="min-w-[220px] flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-gray-300 border-b border-gray-700 pb-1">
                    <Icon className="w-4 h-4 text-purple-400" /> {stage.label}
                    {items.length > 0 && <span className="ml-auto text-gray-600">{items.length}</span>}
                  </div>
                  <div className="text-[10px] text-gray-600 -mt-1">{stage.hint}</div>
                  {items.length === 0 && running && (
                    <div className="text-xs text-gray-700 italic py-2">waiting…</div>
                  )}
                  {items.map((s, i) => <AgentStep key={i} node={s.node} content={s.content} />)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Decision ledger (the ML dataset, human-readable) */}
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
          <Database className="w-3 h-3" /> Decision Ledger <span className="text-gray-700">(training data — outcomes fill in over time)</span>
        </div>
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Ticker</th>
                <th className="text-left px-3 py-2">Paeru</th>
                <th className="text-left px-3 py-2">Brain</th>
                <th className="text-left px-3 py-2">Arbiter</th>
                <th className="text-left px-3 py-2">Leaned</th>
                <th className="text-right px-3 py-2">5d ret</th>
                <th className="text-right px-3 py-2">Mode</th>
              </tr>
            </thead>
            <tbody>
              {decisions.length === 0 && (
                <tr><td colSpan={8} className="text-center text-gray-600 py-6">No decisions yet — run an analysis above.</td></tr>
              )}
              {decisions.map((d) => (
                <tr key={d.decision_id} onClick={() => openDecision(d.decision_id)}
                    className="border-t border-gray-800 hover:bg-gray-800/30 cursor-pointer" title="View full deliberation">
                  <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{new Date(d.ts).toLocaleString()}</td>
                  <td className="px-3 py-2 font-bold">{d.ticker}</td>
                  <td className={`px-3 py-2 ${toneClasses[tone(d.paeru?.action)].split(' ')[0]}`}>{d.paeru?.action || '—'}</td>
                  <td className={`px-3 py-2 ${toneClasses[tone(d.brain?.decision)].split(' ')[0]}`}>{d.brain?.decision || '—'}</td>
                  <td className={`px-3 py-2 font-bold ${toneClasses[tone(d.arbiter?.decision || d.final_action)].split(' ')[0]}`}>{d.arbiter?.decision || d.final_action || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{d.arbiter?.leaned_toward || '—'}</td>
                  <td className={`px-3 py-2 text-right text-xs ${d.outcome?.ret_5d == null ? 'text-gray-600' : d.outcome.ret_5d >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {d.outcome?.ret_5d == null ? '⏳' : (d.outcome.ret_5d * 100).toFixed(1) + '%'}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-gray-500">{d.mode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
